import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/context/AuthContext';
import { declareTourStep, useTourStep } from '../../../shared/lib/TourContext';
import logo from '../../../shared/assets/logos/ARBCOR_Text_only.png';

// IS_DEV is resolved per-user in the component via isSuperadmin
const IS_DEV_HOST = process.env.REACT_APP_DEV_MODE !== 'false'
  && (window.location.hostname === 'dev.arbcor.com' || window.location.hostname === 'localhost');

const navGroups = [
  {
    label: 'Core',
    items: [
      {
        id: 'overview',
        label: 'Overview',
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="2" width="5" height="5" rx="1" />
            <rect x="9" y="2" width="5" height="5" rx="1" />
            <rect x="2" y="9" width="5" height="5" rx="1" />
            <rect x="9" y="9" width="5" height="5" rx="1" />
          </svg>
        ),
      },
      {
        id: 'bills',
        label: 'Bills',
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 2h10a1 1 0 011 1v10l-2-1.5L10 13l-2-1.5L6 13l-2-1.5L2 13V3a1 1 0 011-1z" />
            <path d="M5 6h6M5 9h4" />
          </svg>
        ),
      },
      {
        id: 'purchase-orders',
        label: 'Purchase Orders',
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 2h8a1 1 0 011 1v10a1 1 0 01-1 1H4a1 1 0 01-1-1V3a1 1 0 011-1z" />
            <path d="M6 5h4M6 8h4M6 11h2" />
            <path d="M2 5l2-1M2 8l2-1M2 11l2-1" />
          </svg>
        ),
      },
      {
        id: 'calendar',
        label: 'Calendar',
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="3" width="12" height="11" rx="1.5" />
            <path d="M5 1.5v3M11 1.5v3M2 7h12" />
          </svg>
        ),
      },
      {
        id: 'analytics',
        label: 'Analytics',
        devOnly: true,
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <path d="M2 14V8M6 14V4M10 14V6M14 14V2" />
          </svg>
        ),
      },
      {
        id: 'analytics-pro',
        label: 'Analytics Pro',
        devOnly: true,
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <path d="M2 14V8M6 14V4M10 14V6M14 14V2" />
            <circle cx="13" cy="3" r="2.5" fill="#059669" stroke="none" />
          </svg>
        ),
      },
      {
        id: 'analytics-v2',
        label: 'Analytics Pro V2',
        devOnly: true,
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <path d="M2 14V8M6 14V4M10 14V6M14 14V2" />
            <circle cx="13" cy="3" r="2.5" fill="#1d4ed8" stroke="none" />
          </svg>
        ),
      },
      {
        id: 'enrichment-v3',
        label: 'Enrichment V3',
        devOnly: true,
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 14V8M6 14V4M10 14V6M14 14V2" />
            <path d="M2 8l4-4 4 2 4-4" />
          </svg>
        ),
      },
      {
        id: 'vendor-catalog',
        label: 'Purchases',
        devOnly: false,
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 3h12v10H2z" />
            <path d="M5 3v10" />
            <path d="M8 6h4M8 8.5h3M8 11h2" />
          </svg>
        ),
      },
      {
        id: 'vendor-taxonomy',
        label: 'Vendor Taxonomy',
        devOnly: true,
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 2v4M8 6H4M8 6h4M4 6v3M12 6v3M4 9h0M12 9h0" />
            <circle cx="8" cy="2" r="1" fill="currentColor" />
            <circle cx="4" cy="9" r="1.5" />
            <circle cx="12" cy="9" r="1.5" />
            <path d="M4 10.5v2M12 10.5v2" />
            <circle cx="4" cy="13.5" r="1" />
            <circle cx="12" cy="13.5" r="1" />
          </svg>
        ),
      },
    ],
  },
  {
    label: 'Finance',
    items: [
      {
        id: 'documents',
        label: 'Inbox',
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="3" width="14" height="10" rx="1" />
            <path d="M1 4l7 5 7-5" />
          </svg>
        ),
      },
      {
        id: 'document-detail',
        label: 'Document Detail',
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 2H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V6L9 2Z" />
            <path d="M9 2v4h4" />
            <path d="M5 9h6M5 11.5h4" />
          </svg>
        ),
      },
      {
        id: 'vendors',
        label: 'Vendors',
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="6" cy="5" r="2.5" />
            <path d="M1.5 14c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4" />
            <path d="M11 6.5a2 2 0 1 0 0-4" />
            <path d="M14.5 14c0-2 -1-3.5 -3-3.5" />
          </svg>
        ),
      },
      {
        id: 'vendor-products',
        label: 'Vendor Products',
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 4l6-2 6 2v8l-6 2-6-2z" />
            <path d="M8 6v8M2 4l6 2 6-2" />
          </svg>
        ),
      },
    ],
  },
  {
    label: 'Platform',
    items: [
      {
        id: 'integrations',
        label: 'Integrations',
        devOnly: true,
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="8" cy="4" r="2" />
            <circle cx="4" cy="12" r="2" />
            <circle cx="12" cy="12" r="2" />
            <path d="M8 6V8M6.5 10.5L7.5 8.5M9.5 10.5L8.5 8.5" />
          </svg>
        ),
      },
      {
        id: 'team',
        label: 'Team',
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="6" cy="5" r="2.5" />
            <path d="M1.5 14c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4" />
            <circle cx="11.5" cy="5.5" r="2" />
            <path d="M14.5 14c0-2 1-3 0-3" />
          </svg>
        ),
      },
      {
        id: 'settings',
        label: 'Settings',
        icon: (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <circle cx="8" cy="8" r="2.5" />
            <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3 3l1.5 1.5M11.5 11.5L13 13M13 3l-1.5 1.5M4.5 11.5L3 13" />
          </svg>
        ),
      },
    ],
  },
];

const adminItem = {
  id: 'admin',
  label: 'Admin',
  superadminOnly: true,
  icon: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 1L2 4v4c0 4 2.5 6 6 7 3.5-1 6-3 6-7V4L8 1Z" />
    </svg>
  ),
};

const flatNavItems = navGroups.flatMap((g) => g.items);

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const NavButton = ({ item, activeView, collapsed }) => (
  <Link
    to={item.id === 'overview' ? '/dashboard' : `/dashboard/${item.id}`}
    title={collapsed ? item.label : undefined}
    className={`w-full flex items-center gap-3 px-3 py-2 rounded-sm text-[13px] transition-colors duration-200 relative ${
      item.id === activeView
        ? 'text-white'
        : 'text-zinc-400 hover:text-zinc-200'
    } ${collapsed ? 'justify-center' : ''}`}
  >
    <span
      className={`absolute left-0 top-1/2 -translate-y-1/2 w-[2px] rounded-full bg-white transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${
        item.id === activeView ? 'h-4 opacity-100' : 'h-0 opacity-0'
      }`}
    />
    <span className="shrink-0">{item.icon}</span>
    {!collapsed && item.label}
  </Link>
);

// ---------------------------------------------------------------------------
// Mobile bottom nav — 4 pinned tabs + "More" sheet
// ---------------------------------------------------------------------------

// Primary tabs always visible in the bottom bar (order matters)
const PRIMARY_TAB_IDS = ['overview', 'bills', 'documents', 'vendors'];

const MoreIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8" cy="3" r="1" /><circle cx="8" cy="8" r="1" /><circle cx="8" cy="13" r="1" />
  </svg>
);

const MobileBottomNav = ({ navGroups, adminItem, activeView, isSuperadmin, company }) => {
  const [moreOpen, setMoreOpen] = useState(false);

  // Build all visible items
  const allItems = [];
  navGroups.forEach((group) => {
    group.items.forEach((item) => {
      if ((!item.devOnly || IS_DEV_HOST || isSuperadmin) && (!item.requiresPayables || company?.company?.email_slug)) {
        allItems.push({ ...item, group: group.label });
      }
    });
  });
  if (isSuperadmin) allItems.push({ ...adminItem, group: 'Admin' });

  const primaryTabs = PRIMARY_TAB_IDS.map((id) => allItems.find((i) => i.id === id)).filter(Boolean);
  const moreTabs = allItems.filter((i) => !PRIMARY_TAB_IDS.includes(i.id));
  const activeInMore = moreTabs.some((i) => i.id === activeView);

  // Group moreTabs by group label for the sheet
  const moreGroups = [];
  let lastGroup = null;
  moreTabs.forEach((item) => {
    if (item.group !== lastGroup) {
      moreGroups.push({ label: item.group, items: [] });
      lastGroup = item.group;
    }
    moreGroups[moreGroups.length - 1].items.push(item);
  });

  return (
    <>
      {/* Backdrop */}
      {moreOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/20 backdrop-blur-[2px] z-40"
          onClick={() => setMoreOpen(false)}
        />
      )}

      {/* More sheet */}
      {moreOpen && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-2xl border-t border-zinc-200 max-h-[70vh] overflow-y-auto">
          {/* Handle bar */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-8 h-1 rounded-full bg-zinc-200" />
          </div>

          <div className="px-4 pb-6 space-y-4">
            {moreGroups.map((group) => (
              <div key={group.label}>
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-medium px-1 mb-2">{group.label}</p>
                <div className="grid grid-cols-4 gap-1">
                  {group.items.map((item) => (
                    <Link
                      key={item.id}
                      to={item.id === 'overview' ? '/dashboard' : `/dashboard/${item.id}`}
                      onClick={() => setMoreOpen(false)}
                      className={`flex flex-col items-center gap-1 px-2 py-2.5 rounded-xl transition-colors ${
                        item.id === activeView
                          ? 'text-zinc-900 bg-zinc-100'
                          : 'text-zinc-500 active:bg-zinc-50'
                      }`}
                    >
                      <span className="shrink-0">{item.icon}</span>
                      <span className={`text-[9px] leading-tight text-center ${
                        item.id === activeView ? 'font-medium' : ''
                      }`}>{item.label}</span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bottom bar */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-zinc-100 z-30">
        <div className="flex items-center justify-around px-2 py-1.5">
          {primaryTabs.map((item) => {
            const isActive = item.id === activeView;
            return (
              <Link
                key={item.id}
                to={item.id === 'overview' ? '/dashboard' : `/dashboard/${item.id}`}
                className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-colors min-w-[52px] ${
                  isActive ? 'text-zinc-900' : 'text-zinc-400 active:bg-zinc-50'
                }`}
              >
                <span className="shrink-0">{item.icon}</span>
                <span className={`text-[9px] leading-tight tracking-wide ${isActive ? 'font-medium' : ''}`}>{item.label}</span>
              </Link>
            );
          })}

          {/* More button */}
          <button
            onClick={() => setMoreOpen(!moreOpen)}
            className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-colors min-w-[52px] ${
              activeInMore || moreOpen ? 'text-zinc-900' : 'text-zinc-400 active:bg-zinc-50'
            }`}
          >
            <MoreIcon />
            <span className={`text-[9px] leading-tight tracking-wide ${activeInMore ? 'font-medium' : ''}`}>More</span>
            {activeInMore && !moreOpen && (
              <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-zinc-900" />
            )}
          </button>
        </div>
      </nav>
    </>
  );
};

declareTourStep({
  id: 'sidebar-nav',
  title: 'Navigation',
  description: 'Your control center for bills, vendors, calendars, team setup, and the rest of the workspace.',
  priority: 10,
  path: '/dashboard',
});

const Sidebar = ({ loaded, user, onLogout, activeView = 'overview', onViewChange, company, collapsed, onToggleCollapse }) => {
  const displayName = user?.full_name || 'User';
  const displayEmail = user?.email || '';
  const initials = getInitials(displayName);
  const { companies, switchCompany, isSuperadmin } = useAuth();
  const [companySwitcherOpen, setCompanySwitcherOpen] = useState(false);

  const sidebarNavRef = useTourStep('sidebar-nav');

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex bg-zinc-950 flex-col fixed inset-y-0 left-0 z-30 transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          collapsed ? 'w-16' : 'w-64'
        }`}
        style={{
          opacity: loaded ? 1 : 0,
          transitionProperty: 'width, opacity, transform',
          transitionDuration: loaded ? '300ms' : '1000ms',
          transform: loaded ? 'translateX(0)' : 'translateX(-24px)',
          transitionDelay: loaded ? '0ms' : '100ms',
        }}
      >
        {/* Logo + collapse toggle */}
        <div className={`flex items-center ${collapsed ? 'p-3 justify-center' : 'p-6 pb-8 justify-between'}`}>
          {!collapsed && (
            <Link to="/">
              <img
                src={logo}
                alt="Arbcor"
                className="h-5 w-auto select-none pointer-events-none"
                draggable="false"
                style={{ filter: 'invert(1)' }}
              />
            </Link>
          )}
          <button
            onClick={onToggleCollapse}
            className="text-zinc-600 hover:text-zinc-400 transition-colors p-1"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              {collapsed ? (
                <>
                  <path d="M2 2v12M6 8h8M11 5l3 3-3 3" />
                </>
              ) : (
                <>
                  <path d="M14 2v12M2 8h8M7 5L4 8l3 3" />
                </>
              )}
            </svg>
          </button>
        </div>

        {/* Company switcher */}
        {company && !collapsed && (
          <div className="px-3 pb-4 relative">
            <button
              onClick={() => setCompanySwitcherOpen(!companySwitcherOpen)}
              className="w-full flex items-center justify-between px-3 py-2 rounded-md bg-zinc-900 hover:bg-zinc-800 transition-colors"
            >
              <span className="text-sm text-zinc-300 truncate">{company.company?.name || 'Company'}</span>
              <svg
                width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5"
                className={`text-zinc-500 shrink-0 transition-transform duration-200 ${companySwitcherOpen ? 'rotate-180' : ''}`}
              >
                <path d="M3 5l3 3 3-3" />
              </svg>
            </button>
            {companySwitcherOpen && (
              <div className="absolute left-3 right-3 mt-1 bg-zinc-900 border border-zinc-800 rounded-md shadow-lg z-10 py-1">
                {companies.map((c) => (
                  <button
                    key={c.companyId}
                    onClick={() => {
                      switchCompany(c.companyId);
                      setCompanySwitcherOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                      c.companyId === company.companyId
                        ? 'text-white bg-zinc-800'
                        : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50'
                    }`}
                  >
                    {c.company?.name || 'Unnamed'}
                  </button>
                ))}
                <Link
                  to="/onboarding"
                  onClick={() => setCompanySwitcherOpen(false)}
                  className="w-full block text-left px-3 py-2 text-sm text-zinc-500 hover:text-zinc-300 border-t border-zinc-800 mt-1 pt-2"
                >
                  + Create new company
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Nav */}
        <nav ref={sidebarNavRef} className={`flex-1 overflow-y-auto ${collapsed ? 'px-2' : 'px-3'}`}>
          {navGroups.map((group, gi) => {
            const showDev = IS_DEV_HOST || isSuperadmin;
            const visibleItems = group.items.filter(
              (item) =>
                (!item.devOnly || showDev) &&
                (!item.requiresPayables || company?.company?.email_slug) &&
                (!item.superadminOnly || isSuperadmin)
            );
            if (visibleItems.length === 0) return null;

            return (
              <div key={group.label} className={gi > 0 ? 'mt-6' : ''}>
                {!collapsed && (
                  <div className="px-3 mb-3 text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-medium select-none">
                    {group.label}
                  </div>
                )}
                {collapsed && gi > 0 && (
                  <div className="mx-2 mb-3 border-t border-zinc-800" />
                )}
                <div className="space-y-0.5">
                  {visibleItems.map((item) => (
                    <NavButton key={item.id} item={item} activeView={activeView} collapsed={collapsed} />
                  ))}
                </div>
              </div>
            );
          })}

          {isSuperadmin && (
            <div className="mt-6">
              {collapsed && <div className="mx-2 mb-3 border-t border-zinc-800" />}
              <NavButton item={adminItem} activeView={activeView} collapsed={collapsed} />
            </div>
          )}
        </nav>

        {/* User section */}
        <div className={`border-t border-zinc-900 ${collapsed ? 'p-2' : 'p-4'}`}>
          <button
            onClick={onLogout}
            className={`w-full flex items-center gap-3 hover:opacity-75 transition-opacity ${collapsed ? 'justify-center' : ''}`}
            title={collapsed ? displayName : undefined}
          >
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-xs text-zinc-400 font-medium shrink-0">
                {initials}
              </div>
            )}
            {!collapsed && (
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm text-zinc-300 truncate">{displayName}</p>
                <p className="text-[10px] text-zinc-600 truncate">{displayEmail}</p>
              </div>
            )}
          </button>
        </div>
      </aside>

      {/* Mobile bottom nav — scrollable with auto-center */}
      <MobileBottomNav
        navGroups={navGroups}
        adminItem={adminItem}
        activeView={activeView}
        isSuperadmin={isSuperadmin}
        company={company}
      />
    </>
  );
};

export default Sidebar;
